import { create } from 'zustand';
import { doc, getDoc } from '@/lib/firebase/firestore';
import { db } from '@/config/firebase';
import { callFunction } from '@/lib/firebase/functions';
import { API_ENDPOINTS } from '@/config/constants';
import { useAppStore } from './app-store';
import { useAuthStore } from './auth-store';
import { writeScopedCache } from '@/lib/cache/scoped-cache';

/** Firestore field key for trial start time */
const TRIAL_FIELD = 'ua_app_readonly_trial_start_time';

/** Total trial period in days */
const TRIAL_DAYS = 10;

/**
 * Calculate remaining trial days from a start timestamp.
 * Day 0 (start day) → 10 remaining, day 1 → 9, ..., day 10 → 0 (expired).
 */
function calcDaysRemaining(startTime: number): number {
  const diffMs = Date.now() - startTime;
  const daysSinceStart = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return TRIAL_DAYS - daysSinceStart;
}

interface TrialStore {
  /** Epoch ms when trial started (from Firestore) */
  trialStartTime: number | null;
  /** True once the Firestore read has completed */
  trialLoaded: boolean;
  /** Days remaining in the trial (0 = expired) */
  trialDaysRemaining: number;
  /** True when trial has expired (0 days remaining) */
  isTrialExpired: boolean;
  /** True when trial is active (started and not expired) */
  isTrialActive: boolean;

  /**
   * Called by settings-store when it reads the personal preferences doc.
   * Avoids a second Firestore read by piggybacking on the existing load.
   */
  setTrialFromSettings(timestamp: number | null): void;

  /**
   * If no trial start time exists, write the current timestamp to Firestore.
   * Called once per user per extension (first-time initialization).
   */
  initializeTrialIfNeeded(): Promise<void>;
}

/**
 * Get the Firestore doc path for writing trial start time.
 * Uses the REAL user UID (not impersonated) for write operations.
 * Path: uaAppPreferences/{saasCode}_orgs/{orgId}/{extensionName}/personal/{uid}
 */
function getTrialWriteContext(): { docPath: string; extensionName: string; orgId: string; saasCode: string } | null {
  const uid = useAuthStore.getState().user?.uid;
  const appStore = useAppStore.getState();
  const extensionName = appStore.extensionName;
  const orgId = appStore.appConfig?.orgId;
  const saasCode = appStore.appCodeSAAS;

  if (!uid || !extensionName || !orgId || !saasCode) return null;

  return {
    docPath: `uaAppPreferences/${saasCode}_orgs/${orgId}/${extensionName}/personal/${uid}`,
    extensionName,
    orgId,
    saasCode,
  };
}

interface ResolveTrialStartResponse {
  trialStart: number | null;
  source: 'existing' | 'ledger' | 'fresh' | 'corrupt';
}

// There used to be a module-eval seed from the localStorage trial cache here,
// so the first render had a trial value before any effect ran. It's gone: the
// cache key is now scoped by {uid, appCode} and neither is resolvable at
// module-eval time, so it could only ever return null. Reading the unscoped
// key instead is exactly how a previous account's trial timestamp leaked into
// a fresh login and showed the wrong period.
//
// use-app-init seeds the store from the scoped cache once the scope exists.
// Until then `trialLoaded` stays false, which the UI already gates on
// (settings-view renders "Manage your plan and credits", not a day count).

export const useTrialStore = create<TrialStore>((set, get) => ({
  trialStartTime: null,
  trialLoaded: false,
  trialDaysRemaining: TRIAL_DAYS,
  isTrialExpired: false,
  isTrialActive: false,

  setTrialFromSettings: (timestamp) => {
    if (timestamp && typeof timestamp === 'number' && timestamp > 0) {
      const remaining = calcDaysRemaining(timestamp);
      set({
        trialStartTime: timestamp,
        trialLoaded: true,
        trialDaysRemaining: remaining,
        isTrialExpired: remaining <= 0,
        isTrialActive: remaining > 0,
      });
      // Cache for instant availability on next page load
      writeScopedCache('ua_cache_trial_start', timestamp.toString());
      console.log('[Trial] Loaded — start:', new Date(timestamp).toLocaleDateString(), 'remaining:', remaining, 'days');
    } else {
      // No trial start time found — will be initialized on first visit
      set({
        trialStartTime: null,
        trialLoaded: true,
        trialDaysRemaining: TRIAL_DAYS,
        isTrialExpired: false,
        isTrialActive: false,
      });
      console.log('[Trial] No trial start time found — awaiting initialization');
    }
  },

  initializeTrialIfNeeded: async () => {
    const state = get();

    // Already has a trial start time in memory — nothing to do
    if (state.trialStartTime) return;

    const ctx = getTrialWriteContext();
    if (!ctx) {
      console.log('[Trial] Cannot initialize: missing doc path context');
      return;
    }
    const { docPath, extensionName, orgId, saasCode } = ctx;

    try {
      // Always confirm against Firestore before writing. settings-store can
      // call setTrialFromSettings(null) when (a) the doc has no trial field,
      // (b) the doc exists but the field isn't a valid number, or (c) the
      // settings load errored — only (a) is "no trial yet". Writing on (b)
      // or (c) would overwrite a persisted trial start with `Date.now()`,
      // effectively resetting the user's trial.
      const docRef = doc(db, docPath);
      const snap = await getDoc(docRef);
      const existingTimestamp = snap.exists() ? snap.data()?.[TRIAL_FIELD] : null;

      if (existingTimestamp && typeof existingTimestamp === 'number' && existingTimestamp > 0) {
        // Trial already exists in Firestore — sync it to in-memory store + cache
        get().setTrialFromSettings(existingTimestamp);
        console.log('[Trial] Synced from Firestore — start:', new Date(existingTimestamp).toLocaleDateString());
        return;
      }

      // Only write a fresh start time when the doc genuinely has no trial.
      // If the doc exists but the field is corrupt (non-number), we still
      // skip the write and just mark trial as loaded so we don't clobber
      // unknown data; the user can have it manually corrected.
      if (snap.exists() && existingTimestamp != null) {
        console.warn('[Trial] Existing trial field has unexpected type — skipping init to avoid overwrite', { existingTimestamp });
        set({ trialLoaded: true });
        return;
      }

      // No trial timestamp in Firestore — ask the server for one. It writes
      // the field itself and consults the per-email reset ledger: the first
      // Reset App / Delete Account still yields a fresh trial, later ones get
      // the previous trial date back (no infinite trials).
      // saasCode = alias-resolved appCodeSAAS, so the server writes to the
      // same uaAppPreferences/{saasCode}_orgs path this store reads from.
      const res = await callFunction<{ appCode: string; orgId: string; saasCode: string }, ResolveTrialStartResponse>(
        API_ENDPOINTS.RESOLVE_TRIAL_START,
        { appCode: extensionName, orgId, saasCode }
      );
      if (typeof res?.trialStart === 'number' && res.trialStart > 0) {
        // Use setTrialFromSettings so it also caches to localStorage
        get().setTrialFromSettings(res.trialStart);
        console.log(`[Trial] Initialized (${res.source}) — start:`, new Date(res.trialStart).toLocaleDateString());
      } else {
        console.warn('[Trial] Server returned no trial start', res);
        set({ trialLoaded: true });
      }
    } catch (error) {
      console.error('[Trial] Failed to initialize trial start time:', error);
      // Still mark as loaded so the UI doesn't stay blank
      set({ trialLoaded: true });
    }
  },
}));
